import { useCallback, useEffect, useState } from 'react'
import type { ViewWidth } from '#store/layoutConfiguration'
import { useSharedAppSelector } from '#store/sharedBaseStore'
import { viewWidthSizes } from '#styles/breakpoints'

const getBreakpointSize = (width: number, viewWidth: ViewWidth) => {
  const maxWidth = viewWidthSizes[viewWidth]
  const size = Math.min(width, maxWidth)
  if (size < viewWidthSizes.regular) {
    return 's'
  }
  if (size < viewWidthSizes.wide) {
    return 'm'
  }
  return 'l'
}

/**
 * Hook to keep informed about window sizes, useful to handle layout choices
 * depending on the viewport size;
 */
export const useWindowSize = () => {
  const appSize = useSharedAppSelector((x) => x.layoutConfiguration.width.width)

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    size: getBreakpointSize(window.innerWidth, appSize),
  } as const)

  const updateWindowSize = useCallback(
    (windowWidth: number, windowHeight: number) => {
      setWindowSize({
        width: windowWidth,
        height: windowHeight,
        size: getBreakpointSize(windowWidth, appSize),
      })
    },
    [appSize],
  )

  const [lastAppSize, setLastAppSize] = useState(appSize)
  if (appSize !== lastAppSize) {
    updateWindowSize(windowSize.width, windowSize.height)
    setLastAppSize(appSize)
  }

  useEffect(() => {
    const handleResize = () => {
      updateWindowSize(window.innerWidth, window.innerHeight)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [updateWindowSize])

  return windowSize
}
